import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Ticket,
  Gift,
  Calendar,
  TrendingUp,
  Users,
  Clock,
  AlertCircle,
  ExternalLink,
  Share2,
  Trophy,
  Sparkles,
  ChevronRight,
  Info,
  Filter,
  CheckCircle2,
  XCircle,
  Award,
  Zap,
  Hourglass,
  ArrowUp,
  X,
  Instagram
} from 'lucide-react';
import { couponsService } from '@/services/coupons.service';
import { CampanhaCupom, CupomCliente, SorteioCupom, GanhadorSorteio } from '@/lib/supabase';
import { format, differenceInDays, differenceInHours, differenceInMinutes, isPast, isFuture } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import LinksIndicacao from './LinksIndicacao';
import { userService } from '@/services/user.service';
import { Input } from '@/components/ui/input';

const MeusCupons = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Estados da campanha
  const [campanhaAtiva, setCampanhaAtiva] = useState<CampanhaCupom | null>(null);
  const [cupons, setCupons] = useState<CupomCliente[]>([]);
  const [totalCuponsAtivos, setTotalCuponsAtivos] = useState(0);
  const [totalCuponsInativos, setTotalCuponsInativos] = useState(0);
  const [proximosSorteios, setProximosSorteios] = useState<SorteioCupom[]>([]);
  const [ganhadores, setGanhadores] = useState<GanhadorSorteio[]>([]);
  
  // Estados de UI
  const [selectedCupomGroup, setSelectedCupomGroup] = useState<CupomCliente[] | null>(null);
  const [activeTab, setActiveTab] = useState('meus-cupons');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'ativo' | 'inativo' | 'usado' | 'cancelado'>('todos');
  const [filtroOrigem, setFiltroOrigem] = useState<'todos' | 'compra' | 'indicacao'>('todos');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showLinksIndicacao, setShowLinksIndicacao] = useState(false);
  const [showInstagramCard, setShowInstagramCard] = useState(false);
  const [instagram, setInstagram] = useState('');
  const [loadingInstagram, setLoadingInstagram] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const user = JSON.parse(userDataString);
      setUserData(user);
      
      // Verificar se é cliente
      // if (user.role !== 'cliente') {
      //   toast.error('Esta página é exclusiva para clientes');
      //   navigate('/home');
      //   return;
      // }
      
      // Verificar se precisa preencher Instagram
      if (user.role== 'cliente' && (!user.instagram || user.instagram.trim() === '')) {
        setShowInstagramCard(true);
      }
      
      loadData(user.id);
    } else {
      navigate('/authentication/side-login');
    }
  }, []);

  // Scroll listener para mostrar/ocultar botão de voltar ao topo
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadData = async (clienteId: number) => {
    setLoading(true);
    try {
      // Carregar dados em paralelo
      const [campanha, cuponsData, sorteios, ganhadoresData] = await Promise.all([
        couponsService.getCampanhaAtiva(),
        couponsService.getCuponsCliente(clienteId),
        couponsService.getProximosSorteios(),
        couponsService.getGanhadoresAnteriores()
      ]);

      const totalAtivos = cuponsData.filter(c => c.status === 'ativo').reduce((sum, cupom) => sum + (cupom.quantidade_cupons || 0), 0) || 0;
      const totalInativos = cuponsData.filter(c => c.status === 'inativo' || c.status === 'cancelado').reduce((sum, cupom) => sum + (cupom.quantidade_cupons || 0), 0) || 0;
      setCampanhaAtiva(campanha);
      setCupons(cuponsData);
      setTotalCuponsAtivos(totalAtivos);
      setTotalCuponsInativos(totalInativos);
      setProximosSorteios(sorteios);
      setGanhadores(ganhadoresData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados dos cupons');
    } finally {
      setLoading(false);
    }
  };

  const getCountdownText = (datasorteio: string): string => {
    const agora = new Date();
    const sorteio = new Date(datasorteio);
    
    const dias = differenceInDays(sorteio, agora);
    if (dias > 0) return `${dias} dia${dias > 1 ? 's' : ''}`;
    
    const horas = differenceInHours(sorteio, agora);
    if (horas > 0) return `${horas}h`;
    
    const minutos = differenceInMinutes(sorteio, agora);
    if (minutos > 0) return `${minutos}min`;
    
    return 'Em breve!';
  };



  // Gerar sufixo alfabético (A, B, C, ..., Z, AA, AB, ...)
  const getAlphabetSuffix = (index: number): string => {
    let suffix = '';
    let num = index;
    
    while (num >= 0) {
      suffix = String.fromCharCode(65 + (num % 26)) + suffix;
      num = Math.floor(num / 26) - 1;
    }
    
    return suffix;
  };

  // Gerar lista de códigos com sufixo alfabético
  const gerarCodigosComSufixo = (cupom: CupomCliente): string[] => {
    const codigos: string[] = [];
    for (let i = 0; i < cupom.quantidade_cupons; i++) {
      const sufixo = getAlphabetSuffix(i);
      codigos.push(`${cupom.numero_cupom}-${sufixo}`);
    }
    return codigos;
  };

  const openInstagram = () => {
    window.open('https://www.instagram.com/xdoinfinito/', '_blank');
  };

  const abrirLinksIndicacao = () => {
    setShowLinksIndicacao(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateInstagram = async () => {
    if (!instagram || instagram.trim() === '') {
      toast.error('Por favor, informe seu Instagram');
      return;
    }

    // Remove @ se o usuário digitou
    const cleanInstagram = instagram.replace('@', '').trim();

    setLoadingInstagram(true);
    try {
      await userService.updateInstagram(cleanInstagram, userData.id);
      
      // Atualizar localStorage
      if (userData) {
        const updatedUser = { ...userData, instagram: cleanInstagram };
        localStorage.setItem('userlogged', JSON.stringify(updatedUser));
        setUserData(updatedUser);
      }

      setShowInstagramCard(false);
      toast.success('Instagram atualizado com sucesso! Agora você pode participar das Super Lives.');
    } catch (error) {
      console.error('Erro ao atualizar Instagram:', error);
      toast.error('Erro ao atualizar Instagram. Tente novamente.');
    } finally {
      setLoadingInstagram(false);
    }
  };

  const getCuponsFiltrados = () => {
    let filtrados = cupons;

    // Filtro por status
    if (filtroStatus !== 'todos') {
      filtrados = filtrados.filter(c => c.status === filtroStatus);
    }

    // Filtro por origem
    if (filtroOrigem !== 'todos') {
      filtrados = filtrados.filter(c => c.origem === filtroOrigem);
    }

    return filtrados;
  };

  const getCuponsUsados = () => {
    return cupons.filter(c => c.status === 'usado');
  };

  const getSorteiosRealizados = () => {
    return proximosSorteios.filter(s => s.status === 'realizado');
  };

  const getSorteiosPendentes = () => {
    return proximosSorteios.filter(s => s.status === 'pendente' && isFuture(new Date(s.data_sorteio)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="px-4 pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-2/3"></div>
            <div className="h-32 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Se não tem cupons e não tem campanha ativa
  if ((userData?.role =='cliente' && cupons.length === 0) && !campanhaAtiva) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="px-4 pt-6 pb-20">
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Você ainda não possui cupons</h3>
              <p className="text-muted-foreground mb-4">
                No momento não há campanhas ativas. Quando houver uma nova campanha, você poderá ganhar cupons através de compras e indicações!
              </p>
              <Button onClick={() => navigate('/home-cliente')} className="mt-4">
                Voltar para Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Se não tem cupons mas tem campanha ativa
  if ((userData?.role =='cliente' && cupons.length === 0) && campanhaAtiva) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="px-4 pt-6 pb-20">
          {/* Header da Campanha */}
          {/* <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-6">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h1 className="text-2xl font-bold mb-2">{campanhaAtiva.nome}</h1>
                  <p className="text-sm opacity-90">{campanhaAtiva.descricao}</p>
                </div>
                <Sparkles className="w-8 h-8" />
              </div>
            </CardContent>
          </Card> */}

          {/* Mensagem sem cupons */}
          <Card className="mb-6">
            <CardContent className="p-8 text-center">
              <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Você ainda não possui cupons</h3>
              <p className="text-muted-foreground mb-6">
                A cada <strong>R$100 em compras</strong> ou ou indicações com compras, você ganha cupons para os sorteios semanais e a Super Live!
              </p>

              <Button 
                onClick={() => navigate('/links-indicacao')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Indique amigas e ganhe cupons
              </Button>
            </CardContent>
          </Card>

          {/* Próximos Sorteios */}
          {proximosSorteios.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  Próximos Sorteios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {proximosSorteios.map((sorteio) => (
                  <div key={sorteio.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <p className="font-medium text-sm">{sorteio.titulo}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(sorteio.data_sorteio), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-primary/10">
                      <Clock className="w-3 h-3 mr-1" />
                      {getCountdownText(sorteio.data_sorteio)}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Cliente com cupons - preparar dados
  const cuponsFiltrados = getCuponsFiltrados();
  const cuponsUsados = getCuponsUsados();
  const proximoSorteio = getSorteiosPendentes()[0];
  const sorteiosPendentes = getSorteiosPendentes();
  const sorteiosRealizados = getSorteiosRealizados();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-20">
      {/* Header com Total de Cupons */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className=''>
            <h1 className="text-2xl font-bold">Meus Cupons</h1>
            <p className='max-w-[150px] text-[0.65rem] leading-[1.2]'>Compre e/ou indique e participe dos sorteios da Xizinha.</p>
            {/* <p className="text-sm opacity-90">
              {campanhaAtiva ? (
                <>
                  <span className='text-xs'>Campanha Ativa:</span><br /> <strong>{campanhaAtiva.nome}</strong>
                </>
              ) : 'Histórico de Cupons'}
            </p> */}
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{totalCuponsAtivos}</div>
            <p className="text-xs opacity-90">cupons ativos</p>
          </div>
        </div>

        {/* Countdown do próximo sorteio - apenas se houver campanha ativa */}
        {proximoSorteio && campanhaAtiva && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs opacity-90 mb-1">Próximo sorteio:</p>
                <p className="font-semibold text-sm">{proximoSorteio.titulo}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">{getCountdownText(proximoSorteio.data_sorteio)}</div>
                <p className="text-xs opacity-90">
                  {format(new Date(proximoSorteio.data_sorteio), "dd/MM 'às' HH:mm", { locale: ptBR })}
                </p>
              </div>
            </div>
          </div>
        )}
        <p className='mt-4 mb-2 text-xs'>Siga no instagram e participe das Super Lives!</p>
        <div className="flex items-center justify-between p-3 mt-2 bg-pink-50 rounded-lg border border-pink-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
              <Instagram className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-medium text-pink-800">Instagram</p>
              <p className="text-sm text-pink-700">@xdoinfinito</p>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={openInstagram}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <Instagram className="w-4 h-4 mr-1" />
            Seguir
          </Button>
        </div>

        {/* Alerta se não houver campanha ativa */}
        {!campanhaAtiva && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 mt-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">
                Nenhuma campanha ativa no momento. Visualizando histórico de cupons.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs de Navegação */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4 pt-4">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="meus-cupons" className="text-xs px-[5px]">
            <Ticket className="w-4 h-4 mr-1" />
            Cupons
          </TabsTrigger>
          <TabsTrigger value="sorteios" className="text-xs px-[5px]">
            <Calendar className="w-4 h-4 mr-1" />
            Sorteios
          </TabsTrigger>
          <TabsTrigger value="ganhadores" className="text-xs px-[5px]">
            <Trophy className="w-4 h-4 mr-1" />
            Resultados
          </TabsTrigger>
          <TabsTrigger value="campanha" className="text-xs px-[5px]">
            <Info className="w-4 h-4 mr-1" />
            Campanha
          </TabsTrigger>
        </TabsList>

        {/* TAB: MEUS CUPONS */}
        <TabsContent value="meus-cupons" className="space-y-6 mt-0">
          {/* Ações Rápidas */}
          {campanhaAtiva && (
            <div className={`grid grid-cols-${userData?.role =='cliente' ? 2 : 1} gap-4`}>
              {userData?.role =='cliente' && (
                <Button 
                  onClick={abrirLinksIndicacao}
                  className="bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Indicar Amigas
                </Button>
              )}
              
              
              <Button 
                variant="outline"
                onClick={() => setActiveTab('campanha')}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Saiba mais
              </Button>
            </div>
          )}

          {/* Alerta sem campanha */}
          {!campanhaAtiva && (
            <Card className="bg-muted border-muted">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm mb-1">Nenhuma campanha ativa</h3>
                    <p className="text-xs text-muted-foreground">
                      Visualizando histórico de cupons de campanhas anteriores.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filtros */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtros Rápidos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                  <Select value={filtroStatus} onValueChange={(v: any) => setFiltroStatus(v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="ativo">Ativos</SelectItem>
                      <SelectItem value="inativo">Inativos</SelectItem>
                      <SelectItem value="cancelado">Cancelados</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Origem</label>
                  <Select value={filtroOrigem} onValueChange={(v: any) => setFiltroOrigem(v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="compra">Compras</SelectItem>
                      <SelectItem value="indicacao">Indicações</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estatísticas Rápidas */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-3 text-center">
                <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <div className="text-xl font-bold text-green-700">{totalCuponsAtivos}</div>
                <p className="text-xs text-green-700">Ativos</p>
              </CardContent>
            </Card>

            {/* <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-3 text-center">
                <Award className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                <div className="text-xl font-bold text-blue-700">{cuponsUsados.length}</div>
                <p className="text-xs text-blue-700">Usados</p>
              </CardContent>
            </Card> */}

            <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
              <CardContent className="p-3 text-center">
                <Hourglass className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                <div className="text-xl font-bold text-gray-700">{totalCuponsInativos}</div>
                <p className="text-xs text-gray-700">Inativos</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-3 text-center">
                <Sparkles className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                <div className="text-xl font-bold text-purple-700">{totalCuponsAtivos + totalCuponsInativos}</div>
                <p className="text-xs text-purple-700">Total</p>
              </CardContent>
            </Card>
          </div>

          {/* Lista de Cupons Filtrados */}
          {cuponsFiltrados.length > 0 ? (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Meus Cupons
                <Badge variant="outline" className="ml-auto">
                  {cuponsFiltrados.reduce((sum, c) => sum + c.quantidade_cupons, 0)} cupons
                </Badge>
              </h2>
              <div className="space-y-3">
                {cuponsFiltrados.map((cupom) => {
                  const diasRestantes = couponsService.getDiasRestantes(cupom.data_validade);
                  const isProximoVencimento = couponsService.isProximoVencimento(cupom.data_validade);
                  const isAtivo = cupom.status === 'ativo';
                  const isUsado = cupom.status === 'usado';
                  
                  return (
                    <Card 
                      key={cupom.id}
                      className={`cursor-pointer hover:shadow-md transition-shadow ${
                        isAtivo 
                          ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-200' 
                          : isUsado 
                          ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200'
                          : 'bg-muted/50 border-muted opacity-60'
                      }`}
                      onClick={() => setSelectedCupomGroup([cupom])}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              isAtivo 
                                ? 'bg-green-200' 
                                : isUsado 
                                ? 'bg-blue-200' 
                                : 'bg-muted'
                            }`}>
                              <span className={`text-xl font-bold ${
                                isAtivo 
                                  ? 'text-green-700' 
                                  : isUsado 
                                  ? 'text-blue-700' 
                                  : 'text-muted-foreground'
                              }`}>{cupom.quantidade_cupons}</span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className={`font-semibold ${
                                  isAtivo 
                                    ? 'text-green-800' 
                                    : isUsado 
                                    ? 'text-blue-800' 
                                    : 'text-muted-foreground'
                                }`}>
                                  {cupom.quantidade_cupons} Cupo{cupom.quantidade_cupons > 1 ? 'ns' : 'm'}
                                </p>
                                <Badge variant={isAtivo ? 'default' : isUsado ? 'secondary' : 'outline'} className="text-xs capitalize">
                                  {cupom.status}
                                </Badge>
                              </div>
                              <p className={`text-xs capitalize ${
                                isAtivo 
                                  ? 'text-green-700' 
                                  : isUsado 
                                  ? 'text-blue-700' 
                                  : 'text-muted-foreground'
                              }`}>
                                {cupom.origem === 'compra' ? '🛍️ Compra' : '👥 Indicação'}
                                {cupom.valor_compra && ` - R$ ${cupom.valor_compra.toFixed(2)}`}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 ${
                            isAtivo 
                              ? 'text-green-600' 
                              : isUsado 
                              ? 'text-blue-600' 
                              : 'text-muted-foreground'
                          }`} />
                        </div>
                        
                        <Separator className={`my-2 ${
                          isAtivo 
                            ? 'bg-green-200' 
                            : isUsado 
                            ? 'bg-blue-200' 
                            : 'bg-muted'
                        }`} />
                        
                        <div className="flex items-center justify-between text-xs">
                          <span className={isAtivo || isUsado ? 'text-foreground' : 'text-muted-foreground'}>
                            {format(new Date(cupom.data_geracao), 'dd/MM/yyyy', { locale: ptBR })}
                          </span>
                          {isAtivo && (
                            <div className="flex items-center gap-1">
                              {isProximoVencimento && (
                                <AlertCircle className="w-3 h-3 text-orange-600" />
                              )}
                              <span className={isProximoVencimento ? 'text-orange-600 font-medium' : 'text-green-700'}>
                                {diasRestantes > 0 ? `Validade: ${diasRestantes} dias` : 'Vencido'}
                              </span>
                            </div>
                          )}
                          {isUsado && (
                            <span className="text-blue-700 font-medium flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Sorteado
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">
                  Nenhum cupom encontrado com os filtros selecionados
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: SORTEIOS */}
        <TabsContent value="sorteios" className="space-y-6 mt-0">
          {/* Próximos Sorteios */}
          {sorteiosPendentes.length > 0 && (
            <Card>
              <CardHeader className='px-4'>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Próximos Sorteios
                  <Badge variant="secondary" className="ml-auto">{sorteiosPendentes.length}</Badge>
                </CardTitle>
                <CardDescription>
                  Atenção às datas! Os sorteios estão chegando — garanta seus cupons e aproveite a chance de ganhar prêmios incríveis.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 px-4">
                {sorteiosPendentes.map((sorteio, index) => (
                  <Card key={sorteio.id} className={index === 0 ? 'bg-primary/5 border-primary/20' : 'bg-muted/50'}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-[75%]">
                          <h3 className="font-semibold text-sm">{sorteio.titulo}</h3>
                          {index === 0 && <Badge variant="default" className="text-xs">Próximo</Badge>}
                          <Badge variant="outline" className="mt-1 ml-1 border-gray-400">
                            {sorteio.tipo === 'super_live' ? '⭐ Super Live' : '📅 Semanal'}
                          </Badge>
                        </div>
                        <div className="text-right ml-3 w-[25%]">
                          <div className="text-xl font-bold text-primary">{getCountdownText(sorteio.data_sorteio)}</div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        📅 {format(new Date(sorteio.data_sorteio), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                      {sorteio.descricao && (
                        <p className="text-xs text-muted-foreground">{sorteio.descricao}</p>
                      )}
                      {sorteio.premio_descricao && (
                        <>
                          <Separator className="my-2" />
                          <div className="flex items-start gap-2">
                            <Gift className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-xs font-medium whitespace-pre-wrap">Prêmio:<br/>{sorteio.premio_descricao}</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Sorteios Realizados */}
          {sorteiosRealizados.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Sorteios Realizados
                  <Badge variant="outline" className="ml-auto">{sorteiosRealizados.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sorteiosRealizados.map((sorteio) => (
                  <div key={sorteio.id} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{sorteio.titulo}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(sorteio.data_sorteio), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </p>
                        {sorteio.premio_descricao && (
                          <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">🎁 {sorteio.premio_descricao}</p>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        Realizado
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Sem sorteios */}
          {sorteiosPendentes.length === 0 && sorteiosRealizados.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Nenhum sorteio cadastrado</h3>
                <p className="text-muted-foreground text-sm">
                  Aguarde! Em breve teremos sorteios disponíveis.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: GANHADORES */}
        <TabsContent value="ganhadores" className="space-y-6 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Ganhadores Anteriores
                <Badge variant="secondary" className="ml-auto">{ganhadores.length}</Badge>
              </CardTitle>
              <CardDescription>
                Confira os ganhadores das últimas edições e inspire-se!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {ganhadores.length === 0 ? (
                <div className="p-8 text-center">
                  <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">
                    Ainda não há ganhadores nesta campanha
                  </p>
                </div>
              ) : (
                ganhadores.map((ganhador, index) => (
                  <Card key={ganhador.id} className="bg-gradient-to-r from-gold-light/20 to-gold/20 border-gold-light">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold-light flex items-center justify-center flex-shrink-0">
                          <Trophy className="w-5 h-5 text-gold" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-xs">
                              {ganhador.cliente_nome}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            📍 {ganhador.cliente_cidade}/{ganhador.cliente_estado}
                          </p>
                          {ganhador.premio_descricao && (
                            <div className="flex items-start gap-2 p-2 rounded bg-background/50">
                              <Gift className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span className="text-xs font-medium whitespace-pre-wrap">{ganhador.premio_descricao}</span>
                            </div>
                          )}                         
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: CAMPANHA */}
        <TabsContent value="campanha" className="space-y-6 mt-0">
          {campanhaAtiva ? (
            <>
              {/* Informações da Campanha */}
              <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-2">{campanhaAtiva.nome}</h2>
                      <p className="text-sm opacity-90 mb-3">{campanhaAtiva.descricao}</p>
                      <div className="flex items-center gap-2 text-xs opacity-90">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {format(new Date(campanhaAtiva.periodo_inicio), 'dd/MM/yyyy', { locale: ptBR })} até{' '}
                          {format(new Date(campanhaAtiva.periodo_fim), 'dd/MM/yyyy', { locale: ptBR })}
                        </span>
                      </div>
                    </div>
                    <Sparkles className="w-8 h-8" />
                  </div>
                </CardContent>
              </Card>

              {/* Como Funciona */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Como Funciona
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-green-600">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Ganhe Cupons</h4>
                      <p className="text-xs text-muted-foreground">
                        A cada R$100 em compras = 1 cupom automático. Indique amigas e ganhe cupons extras!
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-blue-600">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Participe dos Sorteios</h4>
                      <p className="text-xs text-muted-foreground">
                        Sorteios semanais toda quinta-feira + Super Live no dia 15 de cada mês.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-purple-600">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Ganhe Prêmios</h4>
                      <p className="text-xs text-muted-foreground">
                        Toda semana tem sorteio! Fique de olho nas datas e esteja presente na super live para validar seu prêmio. Ah, e sua revendedora também ganha!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Regulamento */}
              {campanhaAtiva.regulamento_url && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open(campanhaAtiva.regulamento_url!, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver Regulamento Completo
                </Button>
              )}

              {/* Mensagem Motivacional */}
              <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 shadow-md">
                <CardContent className="p-6 text-center">                               
                  <p className="text-sm font-semibold text-amber-800 mb-3 leading-relaxed">
                    Quanto mais compras e indicações você tiver, maiores suas chances de ganhar!
                  </p>
                  <Separator className="my-3 bg-amber-200" />
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Cada indicação confirmada te aproxima dos prêmios! 🎁 Convide quem você gosta pra viver isso com você — e torçam juntas nos sorteios!
                  </p>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Nenhuma campanha ativa</h3>
                <p className="text-muted-foreground text-sm">
                  No momento não há campanhas de sorteios disponíveis. Fique de olho, em breve teremos novidades!
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Botão Voltar ao Topo */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-8 right-4 z-50 h-10 w-10 rounded-full p-0 shadow-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          aria-label="Voltar ao topo"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}

      {/* Modal de Detalhes do Cupom */}
      <Dialog open={!!selectedCupomGroup} onOpenChange={() => setSelectedCupomGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes dos Cupons</DialogTitle>
            <DialogDescription>
              Informações completas sobre este grupo de cupons
            </DialogDescription>
          </DialogHeader>
          {selectedCupomGroup && selectedCupomGroup.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total de Cupons</p>
                  <p className="text-2xl font-bold">
                    {selectedCupomGroup[0].quantidade_cupons}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Origem</p>
                  <Badge className="mt-1 capitalize" variant='secondary'>
                    {selectedCupomGroup[0].origem == 'indicacao' ? 'Indicação' : 'Compra'}
                    {selectedCupomGroup[0].origem === 'compra' && (
                      // venda id muted
                      <span className='text-muted-foreground ml-1'> #{selectedCupomGroup[0].venda_id}</span>
                    )}
                    {selectedCupomGroup[0].origem === 'indicacao' && (
                      // venda id muted
                      <span className='text-muted-foreground ml-1'> #{selectedCupomGroup[0].indicacao_id}</span>
                    )}

                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 items-center">

                {selectedCupomGroup[0].valor_compra && (
                  <div>
                    <p className="text-sm text-muted-foreground">Valor da Compra</p>
                    <p className="text-xl font-bold">
                      R$ {(selectedCupomGroup[0].valor_compra).toFixed(2)}
                    </p>
                  </div>
                )}
                <div className='text-xs'>
                  <div>
                    <p className="text-muted-foreground">Gerado em</p>
                    <p className="font-medium">
                      {format(new Date(selectedCupomGroup[0].data_geracao), 'dd/MM/yyyy', { locale: ptBR })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Válido até</p>
                    <p className="font-medium">
                      {format(new Date(selectedCupomGroup[0].data_validade), 'dd/MM/yyyy', { locale: ptBR })}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {selectedCupomGroup[0].status === 'cancelado' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <h3 className="font-semibold text-red-800 text-sm">Motivo do Cancelamento</h3>
                  </div>
                  <p className="text-sm text-red-700 leading-relaxed">
                    {selectedCupomGroup[0].motivo_cancelamento || 'Nenhum motivo informado'}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground mb-2">Códigos dos Cupons</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {gerarCodigosComSufixo(selectedCupomGroup[0]).map((codigo, index) => (
                    <div key={index} className="p-2 bg-muted rounded text-sm font-mono flex items-center justify-between">
                      <span>{codigo}</span>
                      <Badge variant="outline" className="text-xs">
                        {index + 1}/{selectedCupomGroup[0].quantidade_cupons}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Links de Indicação */}
      {showLinksIndicacao && (
        <LinksIndicacao 
          hideRevendedoraCard={true}
          onClose={() => setShowLinksIndicacao(false)}
        />
      )}

      {/* Card fixo inferior para coletar Instagram */}
      {showInstagramCard && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 shadow-2xl z-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold mb-1">Participe das Super Lives!</h3>
                <p className="text-[0.85rem] text-white/90">
                  Para garantir sua premiação, é necessário informar seu @ no Instagram e estar presente na live do sorteio.
                </p>
              </div>
              <button
                onClick={() => setShowInstagramCard(false)}
                className="text-white/80 hover:text-white transition-colors flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex flex-row sm:flex-row gap-2 items-end">
              <div className="w-[70%]">
                <Input
                  type="text"
                  placeholder="@seu_instagram"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  className="text-sm bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:bg-white/30"
                  disabled={loadingInstagram}
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  onClick={handleUpdateInstagram}
                  disabled={loadingInstagram}
                  className="flex-1 sm:flex-none bg-white text-purple-600 hover:bg-white/90 font-semibold"
                >
                  {loadingInstagram ? 'Salvando...' : 'Participar'}
                </Button>
                <Button
                  onClick={() => setShowInfoModal(true)}
                  className="flex-1 sm:flex-none bg-white/25 text-white hover:bg-white/35 font-semibold border border-white/40"
                >
                  Saiba +
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de informações sobre Super Lives */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Super Lives - Como Participar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pr-4">
            <p>
              As <strong>Super Lives</strong> são transmissões especiais realizadas todo <strong>dia 15 de cada mês</strong>, 
              no Instagram <strong>@xdoinfinito</strong>, com sorteios ao vivo. Para ganhar, é preciso estar presente, 
              ter informado seu @ no cadastro e responder quando for chamada. Se não responder no momento, outro cupom é sorteado.
            </p>
            
            <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded">
              <h4 className="font-semibold text-purple-900 mb-2">📌 Requisitos para Participar</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Ter cupons ativos e válidos</li>
                <li>Informar seu @ do Instagram no cadastro</li>
                <li>Estar presente na transmissão ao vivo</li>
                <li>Responder quando for chamado(a) durante a live</li>
              </ul>
            </div>

            <div className="bg-pink-50 border-l-4 border-pink-600 p-4 rounded">
              <h4 className="font-semibold text-pink-900 mb-2">🎁 Como Funciona</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Os cupons são sorteados ao vivo durante a live</li>
                <li>Se seu nome for sorteado, você precisa responder na hora para validar seu prêmio</li>
                <li>Os prêmios são entregues apenas para quem estiver presente</li>
              </ul>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded">
              <p className="text-sm font-semibold text-yellow-900 mb-2">⚠ Importante</p>
              <p className="text-sm text-yellow-800">
                Seu Instagram é usado apenas para contato e validação do prêmio.<br />
                Fique atenta às redes sociais e aqui na Xizinha para acompanhar a hora exata da Super Live!
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MeusCupons;
